import moment from 'moment';
import type { DentalOfficeShiftCardDate } from '@tempmee/react-blocks';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';

/** Parses a raw `local_date` ("Thu, Jul 9, 2026") into the DateStamp's parts. */
export const parseCardDate = (localDate?: string): DentalOfficeShiftCardDate | undefined => {
  if (!localDate) {
    return undefined;
  }
  const parsed = moment(localDate, 'ddd, MMM D, YYYY');
  if (!parsed.isValid()) {
    return undefined;
  }
  return {
    weekday: parsed.format('ddd').toUpperCase(),
    day: parsed.date(),
    month: parsed.format('MMM').toUpperCase(),
  };
};

/** Builds a "7 hrs" / "1 hr" duration label from two time-of-day strings. */
export const computeHours = (start?: string, end?: string): string | undefined => {
  if (!start || !end) {
    return undefined;
  }
  const startMoment = moment(start, ['h:mm A', 'hh:mm A']);
  const endMoment = moment(end, ['h:mm A', 'hh:mm A']);
  if (!startMoment.isValid() || !endMoment.isValid()) {
    return undefined;
  }
  let minutes = endMoment.diff(startMoment, 'minutes');
  if (minutes < 0) {
    minutes += 24 * 60; // shift crosses midnight
  }
  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
};

/** Formats a numeric hourly rate as "$85/hr" (undefined when there is no numeric rate). */
export const formatRate = (rate?: number): string | undefined =>
  typeof rate === 'number' ? `$${rate}/hr` : undefined;

/**
 * Resolves the profession badge text. The block shows the short code (e.g. "RDH", "DA"):
 * a sub-profession value is already a code (e.g. "EFDA"); otherwise the code is derived
 * from the profession name, falling back to the raw name. Guards `getCode` so a missing
 * `professionName` never blows up (the two source shapes disagree on whether it is
 * required).
 */
/**
 * The profession's **display name**, e.g. "Expanded Functions DA" rather than the
 * bare "EFDA" code — the counterpart to {@link resolveProfession}, which returns
 * the code.
 *
 * The sub-profession spelling belongs to `UserSubProfession.getName`; this only
 * supplies the fallback order. Four call sites were writing the same expression
 * inline (`JobSummaryRating` twice, plus both Shift Details Redesign pages),
 * which is one map entry away from disagreeing.
 *
 * Returns `undefined` rather than `''` so a caller that renders a slot can omit
 * it; callers needing a string coalesce at the edge.
 */
export const resolveProfessionName = (
  professionName?: string,
  subProfession?: string | null,
): string | undefined =>
  (subProfession ? UserSubProfession.getName(subProfession) : undefined) ??
  professionName ??
  undefined;

export const resolveProfession = (
  professionName?: string,
  subProfession?: string | null,
): string | undefined =>
  subProfession ??
  (professionName ? UserProfession.getCode(professionName) : undefined) ??
  professionName ??
  undefined;
